/* eslint-disable no-underscore-dangle */
import React, {
  useState,
  useEffect,
  FormEvent,
  ChangeEvent,
  MouseEvent,
  FunctionComponent,
} from 'react';

import { MongoDocumentId, Url, Timeout } from '../../interfaces/common';
import { Site, SiteMap } from '../../interfaces/sites';
import { PAGE_SIZE, MAX_RETRIES, SEARCH_DELAY } from '../../constants/sites';
import { siteFilter, getRetryStepDelay, omitHttp } from '../../utils/sites';
import Image from '../image/Image';
import {
  createSite,
  fetchSites,
  fetchCurrentUrl,
  fetchSiteWithDelay,
} from '../../services/sites';

import './Screenshot.scss';

const Screenshot: FunctionComponent = () => {
  const [retryStep, setRetryStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [uuid, setUUID] = useState<MongoDocumentId | null>(null);
  const [info, setInfo] = useState<Site | null>(null);
  const [placeholder, setPlaceholder] = useState<string>('Site url');
  const [sites, setSites] = useState<Site[]>([]);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState<string>('');
  const [hdSites, setHDSites] = useState<SiteMap>({});
  useEffect(() => {
    if (uuid && retryStep < MAX_RETRIES) {
      return fetchSiteWithDelay(
        uuid,
        getRetryStepDelay(retryStep),
        () => setRetryStep(retryStep + 1),
        (site: Site) => {
          setInfo(site);
          setLoading(false);
        },
      );
    }

    setLoading(false);
    return undefined;
  }, [uuid, retryStep]);

  useEffect(() => {
    fetchCurrentUrl((url: Url): void => setPlaceholder(`Site url ${url}`));
    fetchSites(setSites);
  }, []);

  let searchTimeout: Timeout;
  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    clearTimeout(searchTimeout);
    if (info) {
      setSites([info, ...sites]);
      setInfo(null);
    }

    const { value } = event.target;
    searchTimeout = setTimeout(() => {
      setSearch(value);
    }, SEARCH_DELAY);
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setRetryStep(0);
    clearTimeout(searchTimeout);
    setSearch('');
    const form = event.target as HTMLFormElement;
    const element = form.elements.namedItem('url') as HTMLInputElement;
    const documentId = await createSite({ url: element.value });
    setUUID(documentId);
  };

  const handleShowMore = (event: MouseEvent): void => {
    event.preventDefault();
    setPageSize(pageSize + PAGE_SIZE);
  };

  const handleHD = (site: Site): void => {
    setHDSites({ ...hdSites, [site._id]: site });
  };

  const filteredSites = sites.filter(siteFilter(search));
  return (
    <div className="text-center">
      <h1 className="m15">Site screenshot</h1>
      {loading && <div>Loading... (10-20 sec)</div>}
      {!loading && (
        <div className="m15">
          <form onSubmit={handleSubmit}>
            <input
              name="url"
              type="text"
              placeholder={placeholder}
              onChange={handleSearch}
            />
          </form>
        </div>
      )}
      {info && info.thumbnail && (
        <div className="url-item m15">
          <img
            src={info.thumbnail.cloudStorage.publicUrl}
            alt={info.thumbnail.cloudStorage.object}
          />
        </div>
      )}
      {sites.length > 0 && <h2 className="m15 mt30">Sites</h2>}
      <div className="masonry">
        {filteredSites.slice(0, pageSize).map((site) => (
          <div key={site._id} className="item">
            <div className="item-header m15">
              <h3>
                <a
                  href={site.url}
                  title={site.url}
                  target="blank"
                  rel="noopener"
                >
                  {omitHttp(site.url)}
                </a>
              </h3>
            </div>
            <Image
              title={site.url}
              alt={site.original.cloudStorage.object}
              src={
                hdSites[site._id]
                  ? site.original.cloudStorage.publicUrl
                  : site.thumbnail.cloudStorage.publicUrl
              }
            />
            <div className="item-footer m15">
              <div className="text-muted fs-12">
                {new Date(site.createdAt).toLocaleString()}
              </div>
              <div>
                <button
                  type="button"
                  title="Show HD source"
                  onClick={() => handleHD(site)}
                  className={hdSites[site._id] ? 'hidden' : 'visible'}
                >
                  HD
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="m15">
        {filteredSites.length > pageSize && (
          <a href="/" onClick={handleShowMore}>
            Show more
          </a>
        )}
      </div>
    </div>
  );
};

export default Screenshot;
